/**
 * APPLICATION:THEG_MOVIES_LIBRARY — account-switch / member-change isolation.
 * Canon: docs/applications/THEG_MOVIES_LIBRARY_UI_ARCHITECTURE_V1.md §11
 *         docs/applications/THEG_MOVIES_LIBRARY_MEMBER_ONBOARDING_ARCHITECTURE_V1.md §12
 *
 * Security boundary: before another member loads, clear previous member private
 * session, storage, caches, and reset backend target so RUNYI re-resolves
 * member/project/infra via assignments/catalog only (never hardcode targets).
 *
 * No invented credentials/action_keys. Movie DNA calibration untouched —
 * only member-derived DNA *result* cache keys are cleared.
 * AGENT:GROK_BOT — Step4 2026-10-01
 */

export type ThegMemberRuntimeTarget = {
  /** Opaque member identity key from central RUNYI resolution (not hardcoded). */
  memberKey: string | null;
  /** Tenant project ref from RUNYI assignment resolution (never hardcoded). */
  projectRef: string | null;
  /** Application assignment slot when known. */
  assignmentSlot: string | null;
  /** ISO timestamp of last successful resolution. */
  resolvedAt: string | null;
};

export type ThegIsolationReport = {
  clearedLocalStorageKeys: string[];
  clearedSessionStorageKeys: string[];
  previousProjectRef: string | null;
  previousMemberKey: string | null;
  runtimeTargetReset: boolean;
  eventDispatched: boolean;
  at: string;
};

/** Storage key prefixes owned by THEG member-private UI state. */
export const THEG_MEMBER_PRIVATE_PREFIXES = [
  "theg:",
  "theg-movies:",
  "runyi:theg:",
] as const;

/** Known THEG-scoped subkeys (also covered by prefixes; listed for docs/tests). */
export const THEG_MEMBER_PRIVATE_KEYS = [
  "theg:runtime-target",
  "theg:active-member-key",
  "theg:active-project-ref",
  "theg:movie-cache",
  "theg:search-results",
  "theg:retrieval-results",
  "theg:open-movie",
  "theg:open-screenplay",
  "theg:jobs-context",
  "theg:dna-overlap-results",
  "theg:chatgpt-context",
  "theg:media-refs",
  "theg:workspace-nav",
  "theg:selection-context",
] as const;

export const THEG_ISOLATION_EVENT = "theg:member-isolated";

const RUNTIME_TARGET_KEY = "theg:runtime-target";
const ACTIVE_MEMBER_KEY = "theg:active-member-key";
const ACTIVE_PROJECT_REF_KEY = "theg:active-project-ref";

function safeStorage(kind: "local" | "session"): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function collectMatchingKeys(storage: Storage | null, prefixes: readonly string[]): string[] {
  if (!storage) return [];
  const out: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key) continue;
    if (prefixes.some((p) => key === p || key.startsWith(p))) out.push(key);
  }
  return out;
}

export function readThegRuntimeTarget(): ThegMemberRuntimeTarget | null {
  const ls = safeStorage("local");
  if (!ls) return null;
  try {
    const raw = ls.getItem(RUNTIME_TARGET_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ThegMemberRuntimeTarget;
    return {
      memberKey: parsed.memberKey ?? null,
      projectRef: parsed.projectRef ?? null,
      assignmentSlot: parsed.assignmentSlot ?? null,
      resolvedAt: parsed.resolvedAt ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Persist the RUNYI-resolved member backend target.
 * Call only after central assignment resolution — do not invent project refs.
 */
export function writeThegRuntimeTarget(target: ThegMemberRuntimeTarget): void {
  const ls = safeStorage("local");
  if (!ls) return;
  const normalized: ThegMemberRuntimeTarget = {
    memberKey: target.memberKey ?? null,
    projectRef: target.projectRef ?? null,
    assignmentSlot: target.assignmentSlot ?? null,
    resolvedAt: target.resolvedAt ?? new Date().toISOString(),
  };
  ls.setItem(RUNTIME_TARGET_KEY, JSON.stringify(normalized));
  if (normalized.memberKey) ls.setItem(ACTIVE_MEMBER_KEY, normalized.memberKey);
  else ls.removeItem(ACTIVE_MEMBER_KEY);
  if (normalized.projectRef) ls.setItem(ACTIVE_PROJECT_REF_KEY, normalized.projectRef);
  else ls.removeItem(ACTIVE_PROJECT_REF_KEY);
}

/**
 * Clear previous member private UI/session/storage and reset backend target pointer.
 * Also removes enterprise member-auth key for the previous project_ref when provided
 * (matches live web pattern: runyi:member-auth:${project_ref}).
 */
export function clearThegMemberPrivateState(opts?: {
  previousProjectRef?: string | null;
  previousMemberKey?: string | null;
}): ThegIsolationReport {
  const previousProjectRef =
    opts?.previousProjectRef ??
    safeStorage("local")?.getItem(ACTIVE_PROJECT_REF_KEY) ??
    readThegRuntimeTarget()?.projectRef ??
    null;
  const previousMemberKey =
    opts?.previousMemberKey ??
    safeStorage("local")?.getItem(ACTIVE_MEMBER_KEY) ??
    readThegRuntimeTarget()?.memberKey ??
    null;

  const ls = safeStorage("local");
  const ss = safeStorage("session");

  const localKeys = collectMatchingKeys(ls, THEG_MEMBER_PRIVATE_PREFIXES);
  const sessionKeys = collectMatchingKeys(ss, THEG_MEMBER_PRIVATE_PREFIXES);

  // Explicit known keys (idempotent if already matched by prefix).
  for (const key of THEG_MEMBER_PRIVATE_KEYS) {
    if (ls && !localKeys.includes(key) && ls.getItem(key) !== null) localKeys.push(key);
    if (ss && !sessionKeys.includes(key) && ss.getItem(key) !== null) sessionKeys.push(key);
  }

  // Previous member auth storage (enterprise companion key; scoped by project_ref).
  if (previousProjectRef && ls) {
    const memberAuthKey = `runyi:member-auth:${previousProjectRef}`;
    if (ls.getItem(memberAuthKey) !== null && !localKeys.includes(memberAuthKey)) {
      localKeys.push(memberAuthKey);
    }
  }

  for (const key of localKeys) {
    try {
      ls?.removeItem(key);
    } catch {
      /* ignore quota / private mode */
    }
  }
  for (const key of sessionKeys) {
    try {
      ss?.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  // Ensure runtime target pointer is null after isolation (backend must re-resolve).
  let runtimeTargetReset = true;
  try {
    ls?.removeItem(RUNTIME_TARGET_KEY);
    ls?.removeItem(ACTIVE_MEMBER_KEY);
    ls?.removeItem(ACTIVE_PROJECT_REF_KEY);
  } catch {
    runtimeTargetReset = false;
  }

  let eventDispatched = false;
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(THEG_ISOLATION_EVENT, {
          detail: {
            previousProjectRef,
            previousMemberKey,
            at: new Date().toISOString(),
          },
        }),
      );
      eventDispatched = true;
    } catch {
      eventDispatched = false;
    }
  }

  return {
    clearedLocalStorageKeys: localKeys,
    clearedSessionStorageKeys: sessionKeys,
    previousProjectRef,
    previousMemberKey,
    runtimeTargetReset,
    eventDispatched,
    at: new Date().toISOString(),
  };
}

/**
 * Isolate when authenticated member / backend target changes.
 * No-op when memberKey+projectRef are unchanged (including both null).
 */
export function isolateOnMemberSwitch(
  previous: Pick<ThegMemberRuntimeTarget, "memberKey" | "projectRef"> | null,
  next: Pick<ThegMemberRuntimeTarget, "memberKey" | "projectRef"> | null,
): ThegIsolationReport | null {
  const prevMember = previous?.memberKey ?? null;
  const prevProject = previous?.projectRef ?? null;
  const nextMember = next?.memberKey ?? null;
  const nextProject = next?.projectRef ?? null;

  const changed = prevMember !== nextMember || prevProject !== nextProject;
  if (!changed) return null;

  // Only clear when leaving a prior member/target (or switching between two).
  if (prevMember === null && prevProject === null) return null;

  return clearThegMemberPrivateState({
    previousProjectRef: prevProject,
    previousMemberKey: prevMember,
  });
}

/** Sign-out / explicit switch-account path — always clears. */
export function isolateForAccountSwitch(): ThegIsolationReport {
  return clearThegMemberPrivateState();
}
